import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Lock, Zap, Flame, ArrowRight, Check, Sparkles, AlertTriangle } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
import { PaymentMethods } from './PaymentMethods';
interface ExitIntentUpsellPopupProps {
  open: boolean;
  selectedKitIds: string[];
  onAccept: (complementId: string) => void;
  onDecline: () => void;
}
// Fixed bundle discount: take your main kit + 1 complement and save this much.
const BUNDLE_SAVINGS = 4000;
export function ExitIntentUpsellPopup({
  open,
  selectedKitIds,
  onAccept,
  onDecline
}: ExitIntentUpsellPopupProps) {
  const selectedKits = KITS.filter(k => selectedKitIds.includes(k.id));
  const [chosenComplementId, setChosenComplementId] = useState(UPSELLS[0].id);
  const chosenComplement = UPSELLS.find(u => u.id === chosenComplementId) ?? UPSELLS[0];
  const mainTotal = selectedKits.reduce((sum, k) => sum + k.price, 0);
  const normalTotal = mainTotal + chosenComplement.price;
  const bundlePrice = normalTotal - BUNDLE_SAVINGS;
  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  // ESC closes (counts as decline)
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDecline();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onDecline]);
  return <AnimatePresence data-id="element-334">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onDecline} role="dialog" aria-modal="true" aria-labelledby="exit-upsell-title" data-id="element-335">
          <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="relative bg-cream rounded-[1.75rem] shadow-2xl w-full max-w-lg overflow-hidden my-4 max-h-[95vh] flex flex-col" data-id="element-336">
            {/* Close (decline) */}
            <button onClick={onDecline} aria-label="Cerrar" className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-ink-light hover:text-ink flex items-center justify-center shadow-md transition-colors" data-id="element-337">
              <X className="w-4 h-4" data-id="element-338" />
            </button>
            {/* Header banner */}
            <div className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-900 text-white px-4 sm:px-5 pt-3 sm:pt-5 pb-4 sm:pb-5 text-center overflow-hidden" data-id="element-339">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(252,211,77,0.25),transparent_55%)]" data-id="element-340" />
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/5 rounded-full blur-2xl" data-id="element-341" />
              <div className="absolute -bottom-12 -right-10 w-44 h-44 bg-black/20 rounded-full blur-2xl" data-id="element-342" />
              {/* Focal alert icon */}
              <motion.div initial={{
            scale: 0,
            rotate: -10
          }} animate={{
            scale: 1,
            rotate: 0
          }} transition={{
            type: 'spring',
            stiffness: 260,
            damping: 18,
            delay: 0.05
          }} className="relative inline-flex mb-1.5" data-id="element-343">
                <motion.div animate={{
              scale: [1, 1.08, 1]
            }} transition={{
              duration: 1.6,
              repeat: Infinity
            }} className="absolute inset-0 bg-yellow-300/40 rounded-full blur-md" data-id="element-344" />
                <div className="relative w-8 h-8 sm:w-12 sm:h-12 rounded-full bg-yellow-400 text-red-800 flex items-center justify-center shadow-lg border-2 sm:border-[3px] border-white/20" data-id="element-345">
                  <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6" strokeWidth={2.5} data-id="element-346" />
                </div>
              </motion.div>
              {/* Urgency pill */}
              <motion.div animate={{
            scale: [1, 1.03, 1]
          }} transition={{
            duration: 1.5,
            repeat: Infinity
          }} className="relative inline-flex items-center gap-1 sm:gap-1.5 bg-white/15 backdrop-blur-sm text-yellow-200 px-2 sm:px-2.5 py-0.5 rounded-full font-extrabold text-[8px] sm:text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.16em] mb-1.5 sm:mb-2 border border-yellow-300/40" data-id="element-347">
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-348" />
                <span className="hidden sm:inline" data-id="element-349">
                  Oferta exclusiva · Solo aparece una vez
                </span>
                <span className="sm:hidden" data-id="element-350">Oferta exclusiva · Una vez</span>
                <Flame className="w-2 h-2 sm:w-2.5 sm:h-2.5" data-id="element-351" />
              </motion.div>
              {/* Title */}
              <h2 id="exit-upsell-title" className="relative font-sans font-extrabold text-[15px] sm:text-2xl leading-tight tracking-tight mb-1 sm:mb-1.5" data-id="element-352">
                ¡Pará un segundo!
              </h2>
              {/* Subtitle */}
              <p className="relative text-[11px] sm:text-sm text-white/95 max-w-sm mx-auto leading-snug" data-id="element-353">
                Sumá 1 kit complementario y llevate{' '}
                <span className="font-bold text-yellow-300 underline decoration-yellow-300/60 underline-offset-2" data-id="element-354">
                  ambos productos por solo $
                  {bundlePrice.toLocaleString('es-AR')}
                </span>
                .
              </p>
            </div>
            {/* Body */}
            <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4 flex-1 overflow-y-auto" data-id="element-355">
              {/* Flashing discount badge */}
              <div className="flex justify-center mb-2.5" data-id="element-356">
                <motion.div animate={{
              opacity: [1, 0.55, 1],
              scale: [1, 1.04, 1]
            }} transition={{
              duration: 1,
              repeat: Infinity
            }} className="bg-gradient-to-r from-combo-green to-combo-green/80 text-white px-3 py-1 rounded-full font-extrabold text-[10px] sm:text-[11px] shadow-glow border-2 border-white tracking-wide" data-id="element-357">
                  🎁 2 PRODUCTOS · 1 PRECIO ESPECIAL
                </motion.div>
              </div>
              {/* Main copy */}
              <p className="text-[12px] sm:text-sm text-ink leading-snug mb-3 text-center" data-id="element-358">
                Por solo{' '}
                <strong className="text-combo-green" data-id="element-359">
                  ${bundlePrice.toLocaleString('es-AR')}
                </strong>{' '}
                te llevás tu kit principal{' '}
                <strong data-id="element-360">+ 1 kit complementario a elección</strong>. Aprovechá
                antes de cerrar tu compra.
              </p>
              {/* Complement chooser */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-ink/5 mb-2.5" data-id="element-361">
                <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-2 flex items-center gap-1.5" data-id="element-362">
                  <Sparkles className="w-3 h-3" style={{
                color: '#C9A84C'
              }} data-id="element-363" />
                  Elegí tu kit complementario:
                </div>
                <div className="grid grid-cols-2 gap-1.5" data-id="element-364">
                  {UPSELLS.map(u => {
                const active = u.id === chosenComplementId;
                return <button key={u.id} type="button" onClick={() => setChosenComplementId(u.id)} className={`flex items-center gap-1.5 text-left px-2 py-1.5 rounded-lg border transition-all ${active ? 'border-combo-green bg-combo-green/5 ring-1 ring-combo-green' : 'border-ink/10 hover:border-ink/30'}`} data-id="element-365">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                    backgroundColor: u.color
                  }} data-id="element-366" />
                        <span className="text-[11px] font-medium text-ink leading-tight flex-1 min-w-0" data-id="element-367">
                          {u.name.replace('Kit ', '')}
                        </span>
                        {active && <Check className="w-3 h-3 text-combo-green flex-shrink-0" strokeWidth={3} data-id="element-368" />}
                      </button>;
              })}
                </div>
              </div>
              {/* What's included */}
              <div className="bg-white rounded-xl p-3 shadow-sm border border-ink/5 mb-2.5" data-id="element-369">
                <div className="font-bold text-[10px] text-ink uppercase tracking-wider mb-1.5" data-id="element-370">
                  ✅ Esto te llevás hoy:
                </div>
                <ul className="space-y-1" data-id="element-371">
                  {selectedKits.map(kit => <li key={kit.id} className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-372">
                      <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-373" />
                      <span data-id="element-374">
                        <strong data-id="element-375">{kit.name}</strong>{' '}
                        <span className="text-ink-light text-[10px]" data-id="element-376">
                          (kit principal)
                        </span>
                      </span>
                    </li>)}
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-377">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-378" />
                    <span data-id="element-379">
                      <strong data-id="element-380">{chosenComplement.name}</strong>{' '}
                      <span className="text-ink-light text-[10px]" data-id="element-381">
                        (complemento)
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-1.5 text-[11px] sm:text-[12px] text-ink leading-snug" data-id="element-382">
                    <Check className="w-3 h-3 text-combo-green flex-shrink-0 mt-0.5" strokeWidth={3} data-id="element-383" />
                    <span data-id="element-384">
                      <strong data-id="element-385">Acceso de por vida</strong> + actualizaciones
                    </span>
                  </li>
                </ul>
              </div>
              {/* Pricing block */}
              <div className="bg-gradient-to-br from-cream-dark to-cream-dark/80 rounded-xl p-3 border border-gold/30" data-id="element-386">
                <div className="flex items-center justify-between gap-3" data-id="element-387">
                  <div className="text-left min-w-0" data-id="element-388">
                    <div className="text-[9px] text-ink-light uppercase tracking-wider font-bold" data-id="element-389">
                      Tu kit + complemento
                    </div>
                    <div className="flex items-baseline gap-2 mt-0.5" data-id="element-390">
                      <motion.span animate={{
                    scale: [1, 1.04, 1]
                  }} transition={{
                    duration: 1.5,
                    repeat: Infinity
                  }} className="text-xl sm:text-2xl font-extrabold text-combo-green tabular-nums" data-id="element-391">
                        ${bundlePrice.toLocaleString('es-AR')}
                      </motion.span>
                      <span className="text-[11px] sm:text-xs text-ink-light line-through decoration-red-500 decoration-2 tabular-nums" data-id="element-392">
                        ${normalTotal.toLocaleString('es-AR')}
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0" data-id="element-393">
                    <div className="inline-flex items-center gap-1 bg-combo-green text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-394">
                      Ahorrás ${BUNDLE_SAVINGS.toLocaleString('es-AR')}
                    </div>
                    <div className="text-[9px] text-ink-light mt-1 flex items-center justify-end gap-1" data-id="element-395">
                      <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-396" />
                      Solo en esta pantalla
                    </div>
                  </div>
                </div>
              </div>
              {/* Payment methods */}
              <PaymentMethods className="mt-3" data-id="element-397" />
            </div>
            {/* Sticky CTAs */}
            <div className="px-4 sm:px-6 py-3 sm:py-3.5 bg-white border-t border-ink/5 space-y-1.5" data-id="element-398">
              <motion.button onClick={() => onAccept(chosenComplementId)} whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} animate={{
            boxShadow: ['0 0 0 0 rgba(27,67,50,0.55)', '0 0 0 14px rgba(27,67,50,0)', '0 0 0 0 rgba(27,67,50,0)']
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="w-full bg-combo-green text-white py-2.5 sm:py-3 rounded-full font-extrabold text-[12px] sm:text-sm hover:bg-combo-green/90 transition-colors flex items-center justify-center gap-1.5 shadow-glow" data-id="element-399">
                <span className="truncate" data-id="element-400">
                  ¡SÍ! LLEVAR AMBOS POR ${bundlePrice.toLocaleString('es-AR')}
                </span>
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" data-id="element-401" />
              </motion.button>
              <button onClick={onDecline} className="w-full text-ink-light hover:text-ink text-[11px] sm:text-xs font-medium underline underline-offset-4 transition-colors py-1" data-id="element-402">
                No gracias, continuar solo con mi kit →
              </button>
              <div className="flex items-center justify-center gap-3 text-[9px] sm:text-[10px] text-ink-light font-medium" data-id="element-403">
                <span className="flex items-center gap-1" data-id="element-404">
                  <Lock className="w-2.5 h-2.5 text-combo-green" data-id="element-405" /> Pago Seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-406">
                  <Zap className="w-2.5 h-2.5 text-gold" data-id="element-407" /> Acceso Inmediato
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}